import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import TablePagination from '@material-ui/core/TablePagination';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';

import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

import ViewUserDialog from 'app/components/user/ViewUserDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
    },
    pageTitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(5)
    },
    directory: {
        maxWidth: 680,
        margin: '0 auto',
        minHeight: 460
    },
    paper: {
        ...paper(theme),
        position: 'relative'
    },
    gridItemExpand: {
        flexGrow: 1
    },
    searchField: {
        minWidth: '220px'
    },
    pagination: {
        flexWrap: 'wrap',
        justifyContent: 'flex-end'
    }
}));

export default function Directory(props) {

    const { app } = useApp();

    const [ user, setUser ] = useState(null);
    const [ users, setUsers ] = useState([]);

    const [ searchValue, setSearchValue ] = useState('');
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(10);
    const [ total, setTotal ] = useState(0);

    const [ loading, setLoading ] = useState(false);
    const [ userDialogOpen, setUserDialogOpen ] = useState(false);

    const classes = useStyles();
    const api = useApi();

    // Getter Functions ------------------------------------------

    const getUsers = (newPage = null, newRowsPerPage = null, newSearchValue = null) => {

        setLoading(true);

        const data = {
            page: (newPage === null) ? page : newPage,
            rowsPerPage: (newRowsPerPage === null) ? rowsPerPage : newRowsPerPage,
            searchValue: (newSearchValue === null) ? searchValue : newSearchValue
        }

        api.post('/directory', data)
            .then(res => {
                setUsers(res.users);
                setPage(res.page);
                setTotal(res.total);
                setRowsPerPage(res.rowsPerPage);
            }).finally(() => setLoading(false));

    }

    useEffect(() => {
        getUsers();
    }, []);

    // Setter Functions ------------------------------------------

    const handleSearch = (event, bypass = false) => {

        if (bypass || (event.key && event.key === 'Enter')) {
            event.preventDefault();
            getUsers(0);
        }
    }

    const handleSearchValue = event => {
        setSearchValue(event.target.value);
    }

    const handleChangePage = (event, newPage) => {
        getUsers(newPage)
    };

    const handleChangeRowsPerPage = event => {
        getUsers(page, event.target.value)
    };

    const handleMouseDown = (event) => {
        event.preventDefault();
    };

    const handleClearSearch = () => {
        setSearchValue("");
        getUsers(0, null, "");
    }

    // User Dialog Functions ------------------------------------------

    const openUserDialog = user => {
        setUserDialogOpen(true);
        setUser(user);
    }

    const closeUserDialog = () => {
        setUserDialogOpen(false);
    }

    const userDialogClosed = () => {
        setUser(null);
    }

    // Constants ------------------------------------------

    const emptyRows = rowsPerPage - users.length;

    return (
        <form className={classes.container} noValidate autoComplete="off">
           <Typography align="center" variant="h4" component="h2" className={classes.pageTitle}>
                {app.club.members_group.member_called} Directory
            </Typography>
            <div className={classes.directory}>
                <Paper className={classes.paper}>
                    <Loading isLoading={loading}/>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid item className={classes.gridItemExpand}>
                            <FormControl variant="outlined" fullWidth className={classes.searchField}>
                                <OutlinedInput
                                    id="input-directory-search"
                                    value={searchValue}
                                    onChange={handleSearchValue}
                                    onKeyPress={handleSearch}
                                    placeholder="Search the Directory..."
                                    color="secondary"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Search"
                                                onClick={(event) => handleSearch(event, true)}
                                                onMouseDown={handleMouseDown}
                                                edge="end"
                                                >
                                                <SearchIcon/>
                                            </IconButton>
                                            <IconButton
                                                aria-label="Clear Search Field"
                                                onClick={handleClearSearch}
                                                onMouseDown={handleMouseDown}
                                                edge="end"
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <List dense className={classes.root}>
                        {users.map(user => (
                            <ListItem
                                key={user.id}
                                button
                                onClick={() => openUserDialog(user)}
                                >
                                <ListItemAvatar>
                                    <Avatar
                                        alt={user.full_name}
                                        src={user.avatar ? user.avatar.full_url : null}
                                        >
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={user.full_name}
                                    secondary={user.public_membership_title}
                                />
                            </ListItem>
                        ))}
                        {(emptyRows > 0) &&
                            <ListItem style={{ height: 60 * emptyRows }} />
                        }
                    </List>
                    <TablePagination
                        count={total}
                        component="div"
                        rowsPerPage={rowsPerPage}
                        page={page}
                        backIconButtonProps={{
                            'aria-label': 'Previous Page',
                        }}
                        nextIconButtonProps={{
                            'aria-label': 'Next Page',
                        }}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        classes={{ toolbar: classes.pagination }}
                        />
                </Paper>
            </div>
            <ViewUserDialog
                user={user}
                open={userDialogOpen}
                onClose={closeUserDialog}
                onClosed={userDialogClosed}
            />
        </form>
    );
}
